import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";

const TERMS_TEXT = `1. I understand that participating in yoga involves physical activity, which carries a risk of injury. I voluntarily assume all risks associated with participating in the yoga classes provided by Aham Yog Institute.

2. I confirm that I am in good health and have consulted with a physician if necessary before participating in the yoga classes. I understand that it is my responsibility to inform the instructor of any health conditions or injuries that may affect my ability to participate safely.

3. I consent to the collection and use of my personal information for the purposes of communication, scheduling, and emergency contact. I grant permission to use any photographs or videos taken during classes for promotional purposes.

By accepting below, I acknowledge that I have read and understood this consent form. I agree to the terms and conditions outlined above and consent to participate in the yoga classes provided by Aham Yog Institute.`;

export const emptyEnrollmentProfile = {
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: "",
    nationality: "Indian",
    gender: "",
    pincode: "",
    state: "",
    address: "",
    city: "",
    medical_history: "",
    is_pregnant: "not_applicable",
    medications: "no",
    medications_details: "",
    emergency_contact_name: "",
    emergency_contact_mobile: "",
    terms_accepted: false,
};

// Required keys (middle_name is intentionally optional).
const REQUIRED_KEYS = [
    "first_name", "last_name", "date_of_birth", "nationality", "gender",
    "pincode", "state", "address", "city", "medical_history", "medications",
    "emergency_contact_name", "emergency_contact_mobile",
];

export function isEnrollmentProfileComplete(p) {
    if (!p) return false;
    if (!p.terms_accepted) return false;
    for (const k of REQUIRED_KEYS) {
        const v = p[k];
        if (v === null || v === undefined) return false;
        if (typeof v === "string" && !v.trim()) return false;
    }
    if (p.medications === "yes" && !(p.medications_details || "").trim()) return false;
    return true;
}

function Field({ label, required = false, children, hint }) {
    return (
        <div>
            <Label className="text-sm">
                {label}
                {required && <span className="text-accent-600 ml-0.5">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
            {hint && <p className="text-[11px] text-ink-400 mt-1">{hint}</p>}
        </div>
    );
}

export default function EnrollmentProfileForm({
    initial,
    onSaved,
    submitLabel = "Save profile",
    compact = false,
    showHeader = true,
}) {
    const [form, setForm] = useState({ ...emptyEnrollmentProfile, ...(initial || {}) });
    const [saving, setSaving] = useState(false);
    const isComplete = isEnrollmentProfileComplete(form);

    useEffect(() => {
        if (initial) setForm({ ...emptyEnrollmentProfile, ...initial });
    }, [initial]);

    const set = (key) => (eOrVal) => {
        const value = eOrVal && typeof eOrVal === "object" && "target" in eOrVal ? eOrVal.target.value : eOrVal;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!isComplete) return;
        setSaving(true);
        try {
            const { data } = await api.put("/me/profile", form);
            toast.success("Profile saved");
            onSaved?.(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const isFemale = form.gender === "female";

    return (
        <form onSubmit={submit} className={compact ? "space-y-5" : "space-y-6"} data-testid="enrollment-profile-form">
            {showHeader && (
                <div className="bg-brand-50/60 border border-brand-100 rounded-2xl p-4 flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
                    <div className="text-sm text-ink-900">
                        <div className="font-semibold">Enrollment profile required</div>
                        <p className="text-ink-700 mt-0.5">
                            We need these details once before you can enroll in any course (online or offline).
                            Fields with <span className="text-accent-600">*</span> are mandatory.
                        </p>
                    </div>
                </div>
            )}

            {/* Personal */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink-900">Personal details</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="First name" required>
                        <Input value={form.first_name} onChange={set("first_name")} required maxLength={80} data-testid="profile-first-name" />
                    </Field>
                    <Field label="Middle name">
                        <Input value={form.middle_name} onChange={set("middle_name")} maxLength={80} data-testid="profile-middle-name" />
                    </Field>
                    <Field label="Last name" required>
                        <Input value={form.last_name} onChange={set("last_name")} required maxLength={80} data-testid="profile-last-name" />
                    </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Date of birth" required>
                        <Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} required data-testid="profile-dob" />
                    </Field>
                    <Field label="Nationality" required>
                        <Input value={form.nationality} onChange={set("nationality")} required maxLength={80} data-testid="profile-nationality" />
                    </Field>
                    <Field label="Gender" required>
                        <Select value={form.gender} onValueChange={set("gender")}>
                            <SelectTrigger data-testid="profile-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                                <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                </div>
            </div>

            {/* Address */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink-900">Address</h3>
                <Field label="Address (House no., Building, Street, Locality & Landmark)" required>
                    <Textarea rows={3} value={form.address} onChange={set("address")} required maxLength={400} data-testid="profile-address" />
                </Field>
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Pincode" required>
                        <Input value={form.pincode} onChange={set("pincode")} required maxLength={12} data-testid="profile-pincode" />
                    </Field>
                    <Field label="City" required>
                        <Input value={form.city} onChange={set("city")} required maxLength={80} data-testid="profile-city" />
                    </Field>
                    <Field label="State" required>
                        <Input value={form.state} onChange={set("state")} required maxLength={80} data-testid="profile-state" />
                    </Field>
                </div>
            </div>

            {/* Medical */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink-900">Medical history</h3>
                <Field
                    label="Past surgery, accidents, current conditions & duration"
                    required
                    hint="Please list any medical conditions, injuries, or limitations we should be aware of, and mention how long you have had each."
                >
                    <Textarea
                        rows={4}
                        value={form.medical_history}
                        onChange={set("medical_history")}
                        required
                        maxLength={2000}
                        placeholder="e.g. Lower back pain since 2023, knee surgery in 2019..."
                        data-testid="profile-medical-history"
                    />
                </Field>

                {isFemale && (
                    <Field label="Are you currently pregnant?" required>
                        <Select value={form.is_pregnant === "not_applicable" ? "no" : form.is_pregnant} onValueChange={set("is_pregnant")}>
                            <SelectTrigger data-testid="profile-pregnant"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                )}

                <Field label="Are you taking any medications that may affect your ability to participate in physical activity?" required>
                    <Select value={form.medications} onValueChange={(v) => setForm((f) => ({ ...f, medications: v, medications_details: v === "no" ? "" : f.medications_details }))}>
                        <SelectTrigger data-testid="profile-medications"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                {form.medications === "yes" && (
                    <Field label="Please specify your medications" required>
                        <Textarea
                            rows={2}
                            value={form.medications_details}
                            onChange={set("medications_details")}
                            required
                            maxLength={500}
                            data-testid="profile-medications-details"
                        />
                    </Field>
                )}
            </div>

            {/* Emergency contact */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink-900">Emergency contact</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name of the person to contact in an emergency" required>
                        <Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")} required maxLength={120} data-testid="profile-emergency-name" />
                    </Field>
                    <Field label="Their mobile number" required>
                        <Input
                            value={form.emergency_contact_mobile}
                            onChange={set("emergency_contact_mobile")}
                            required
                            maxLength={20}
                            placeholder="e.g. +91 99999 99999"
                            data-testid="profile-emergency-mobile"
                        />
                    </Field>
                </div>
            </div>

            {/* Terms & conditions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink-900">Terms and conditions</h3>
                <div className="bg-paper-alt rounded-xl p-4 max-h-56 overflow-y-auto text-sm text-ink-700 whitespace-pre-line leading-relaxed" data-testid="profile-terms-text">
                    {TERMS_TEXT}
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.terms_accepted}
                        onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        data-testid="profile-terms-checkbox"
                    />
                    <span className="text-sm text-ink-900">
                        I have read and agree to the terms and conditions.{" "}
                        <span className="text-accent-600">*</span>
                    </span>
                </label>
                {form.terms_accepted_at && (
                    <p className="text-[11px] text-ink-400">Originally accepted on {new Date(form.terms_accepted_at).toLocaleString()}.</p>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                    {isComplete ? (
                        <span className="inline-flex items-center gap-1.5 text-brand-700 font-medium">
                            <CheckCircle2 className="h-4 w-4" /> All required fields filled
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-ink-700">
                            <AlertCircle className="h-4 w-4 text-accent-500" /> Please complete all required fields
                        </span>
                    )}
                </div>
                <Button
                    type="submit"
                    disabled={!isComplete || saving}
                    className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="profile-submit-btn"
                >
                    {saving ? "Saving…" : submitLabel}
                </Button>
            </div>
        </form>
    );
}
